'use client';

import React, { useState, useEffect } from 'react';

interface MockWizardProps {
    isActive: boolean;
    onComplete: () => void;
}

export function MockWizard({ isActive, onComplete }: MockWizardProps) {
    const [internalStep, setInternalStep] = useState(0); // 0: Concept, 1: Audience, 2: Features, 3: Layout, 4: Theme
    const [typedText, setTypedText] = useState('');

    // Reset when active
    useEffect(() => {
        if (isActive) {
            setInternalStep(0);
            setTypedText('');
            startAnimationSequence();
        }
    }, [isActive]);

    const startAnimationSequence = async () => {
        // Step 0: Type Concept (Step 1/10)
        await wait(500);
        await typeText("A platform for creators to validate ideas quickly.");
        await wait(600);

        // Step 1: Target Audience (Step 3/10)
        setInternalStep(1);
        await wait(1200); // Allow time to "read/click"

        // Step 2: Key Features (Step 5/10)
        setInternalStep(2);
        await wait(1200);

        // Step 3: Select Layout (Step 7/10)
        setInternalStep(3);
        await wait(1000);

        // Step 4: Select Theme & Generate (Step 9/10)
        setInternalStep(4);
        await wait(1000);

        // Click Generate
        onComplete();
    };

    const typeText = async (text: string) => {
        for (let i = 0; i <= text.length; i++) {
            setTypedText(text.slice(0, i));
            await wait(20); // Slightly faster typing
        }
    };

    const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    // Helper to calculate visual step number
    const getVisualStep = () => {
        switch (internalStep) {
            case 0: return 1;
            case 1: return 3;
            case 2: return 5;
            case 3: return 7;
            case 4: return 9;
            default: return 1;
        }
    };

    return (
        <div className="w-full h-full flex flex-col bg-[#FAFAFA]">
            {/* Header */}
            <div className="h-16 border-b bg-white flex items-center px-8 justify-between">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold">W</div>
                    <span className="font-semibold text-gray-900">Waitlistly AI</span>
                </div>
                <div className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-sm font-medium">
                    Step {getVisualStep()} of 10
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 w-full max-w-2xl min-h-[400px]">

                    {/* Step 0: Core Concept */}
                    {internalStep === 0 && (
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-6 text-indigo-600">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">The Core Concept</h2>
                            <p className="text-gray-500 mb-8">Every great landing page starts with a clear value proposition.</p>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Describe what it is</label>
                                    <div className="w-full h-32 p-4 rounded-xl border border-indigo-500 ring-4 ring-indigo-500/10 bg-white text-gray-900">
                                        {typedText}<span className="animate-pulse">|</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 1: Target Audience (NEW) */}
                    {internalStep === 1 && (
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-6 text-indigo-600">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Target Audience</h2>
                            <p className="text-gray-500 mb-8">Who is this product for?</p>

                            <div className="space-y-3">
                                <div className="p-4 border-2 border-indigo-600 bg-indigo-50 rounded-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-2 cursor-default">
                                    <span className="font-semibold text-indigo-900">Solo Founders & Indies</span>
                                    <div className="hidden md:block">
                                        <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                    </div>
                                </div>
                                <div className="p-4 border border-gray-200 rounded-xl opacity-60">
                                    <span className="font-semibold text-gray-700">Small Business Owners</span>
                                </div>
                                <div className="p-4 border border-gray-200 rounded-xl opacity-60">
                                    <span className="font-semibold text-gray-700">Marketing Agencies</span>
                                </div>
                            </div>
                            <div className="mt-8 flex justify-end">
                                <button className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium w-full md:w-auto">Next Step →</button>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Key Features (NEW) */}
                    {internalStep === 2 && (
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-6 text-indigo-600">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Key Features</h2>
                            <p className="text-gray-500 mb-8">What makes your product standout?</p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                <div className="p-3 border-2 border-indigo-600 bg-indigo-50 rounded-lg flex gap-3 items-center">
                                    <div className="w-5 h-5 bg-indigo-600 rounded flex items-center justify-center text-white text-xs flex-shrink-0">✓</div>
                                    <span className="font-medium text-indigo-900">Waitlist Collection</span>
                                </div>
                                <div className="p-3 border-2 border-indigo-600 bg-indigo-50 rounded-lg flex gap-3 items-center">
                                    <div className="w-5 h-5 bg-indigo-600 rounded flex items-center justify-center text-white text-xs flex-shrink-0">✓</div>
                                    <span className="font-medium text-indigo-900">Analytics</span>
                                </div>
                                <div className="p-3 border border-gray-200 rounded-lg flex gap-3 items-center opacity-60">
                                    <div className="w-5 h-5 border border-gray-300 rounded flex-shrink-0"></div>
                                    <span className="font-medium text-gray-700">Newsletter</span>
                                </div>
                                <div className="p-3 border border-gray-200 rounded-lg flex gap-3 items-center opacity-60">
                                    <div className="w-5 h-5 border border-gray-300 rounded flex-shrink-0"></div>
                                    <span className="font-medium text-gray-700">Payments</span>
                                </div>
                            </div>
                            <div className="mt-8 flex justify-end">
                                <button className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium w-full md:w-auto">Next Step →</button>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Layout */}
                    {internalStep === 3 && (
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-6 text-indigo-600">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" /></svg>
                            </div>
                            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Select Layout</h2>
                            <p className="text-gray-500 mb-8">Choose a starting framework.</p>

                            <div className="space-y-4">
                                <div className="p-4 border-2 border-indigo-600 bg-indigo-50 rounded-xl cursor-default transition-all">
                                    <div className="font-semibold text-indigo-900">Minimal Modern</div>
                                    <div className="text-sm text-indigo-700">Clean, centered text, focus on typography.</div>
                                </div>
                                <div className="p-4 border border-gray-200 hover:border-gray-300 rounded-xl opacity-60">
                                    <div className="font-semibold text-gray-900">Professional Corporate</div>
                                    <div className="text-sm text-gray-500">Split screen, reliable, trust-building.</div>
                                </div>
                            </div>
                            <div className="mt-8 flex justify-end">
                                <button className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium w-full md:w-auto">Next Step →</button>
                            </div>
                        </div>
                    )}

                    {/* Step 4: Color Theme */}
                    {internalStep === 4 && (
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-6 text-indigo-600">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" /></svg>
                            </div>
                            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Color Theme</h2>
                            <p className="text-gray-500 mb-8">Choose colors that match your brand identity.</p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                <div className="p-4 border-2 border-indigo-600 bg-white rounded-xl flex items-center gap-3 relative overflow-hidden">
                                    <div className="absolute top-0 right-0 p-1 bg-indigo-600 text-white rounded-bl-lg">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                    </div>
                                    <div className="w-10 h-10 rounded-full bg-blue-500 flex-shrink-0"></div>
                                    <div>
                                        <div className="font-semibold text-gray-900">Modern Blue</div>
                                        <div className="text-xs text-gray-500">Tech, Trust, Clean</div>
                                    </div>
                                </div>
                                <div className="p-4 border border-gray-200 bg-white rounded-xl flex items-center gap-3 opacity-60">
                                    <div className="w-10 h-10 rounded-full bg-purple-600 flex-shrink-0"></div>
                                    <div>
                                        <div className="font-semibold text-gray-900">Elegant Purple</div>
                                        <div className="text-xs text-gray-500">Premium, Creative</div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 md:mt-12 flex justify-end">
                                <button className="bg-indigo-600 text-white px-8 py-3 rounded-lg font-bold shadow-lg shadow-indigo-200 transform scale-105 transition-transform w-full md:w-auto">
                                    Generate Site ✨
                                </button>
                            </div>
                        </div>
                    )}


                </div>
            </div>
        </div>
    );
}
